import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  CardText,
  Badge,
  Button,
  ListGroup,
  ListGroupItem,
} from 'reactstrap';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useParams, useLocation } from 'react-router-dom';
import {
  getTruckDriver,
  getTrack,
  getLocations,
} from '../../../../api/TruckDriver/TruckDriver';
import DriverControls from './components/DriverControls';
import TrackingControls from './components/TrackingControls';
import MapDisplay2 from './components/MapDisplay2';
import {
  FaTruck,
  FaUser,
  FaIdBadge,
  FaPhone,
  FaMapMarkerAlt,
  FaClock,
} from 'react-icons/fa';
import './driver.css';
import { User, Car, Calendar, MapPin, Phone, Mail, Search } from 'lucide-react';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const TruckTracking = () => {
  const mapContainer = useRef(null);
  const [truckDriverDetails, setTruckDriverDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trackedVehicle, setTrackedVehicle] = useState(null);
  const [pathCoordinates, setPathCoordinates] = useState([]);
  const [pastLocations, setPastLocations] = useState([]);
  const [showTrack, setShowTrack] = useState(false); // State to control track visibility
  const [driverStatus, setDriverStatus] = useState('pending'); // Dynamic status

  const { truckDriverid, stockNo, tripId } = useParams();
  const location = useLocation();

  const drivers = [
    {
      id: 1,
      name: 'Sarah Johnson',
      phone: '+1 (555) 123-4567',
      email: 'sarah.johnson@email.com',
      location: 'New York, NY',
      vehicle: 'Van',
      plate: 'ABC-1234',
      year: 2023,
      color: 'Pearl White',
      date: '2024-03-15',
      status: driverStatus, // Use dynamic status
      avatar: 'SJ',
    },
  ];

  // SSE for real-time tracking data
  useEffect(() => {
    if (!truckDriverid || !stockNo || !tripId) return;

    const url = `https://api.starmarketapi.online/truck_driver1/track/${truckDriverid}/${stockNo}/${tripId}`;
    const eventSource = new EventSource(url);

    eventSource.onmessage = (event) => {
      try {
        const parsedData = JSON.parse(event.data);
        if (
          parsedData &&
          typeof parsedData.lat === 'number' &&
          typeof parsedData.lon === 'number' &&
          parsedData.timestamp
        ) {
          console.log('✅ Parsed JSON:', parsedData);
          setPathCoordinates((prev) => {
            const newCoordinates = [...prev, parsedData];
            // Update status based on latest timestamp
            const latestTimestamp = new Date(parsedData.timestamp);
            const currentTime = new Date();
            const timeDiff = (currentTime - latestTimestamp) / (1000 * 60); // Difference in minutes
            setDriverStatus(timeDiff <= 15 ? 'live' : 'past');
            return newCoordinates;
          });
        } else {
          console.log('⚠️ Invalid data format:', parsedData);
          setDriverStatus('pending'); // No valid data
        }
      } catch (error) {
        console.log('⚠️ Non-JSON message or parsing error:', event.data, error);
        setDriverStatus('pending');
      }
    };

    eventSource.onerror = () => {
      console.log('❌ SSE error, closing connection');
      eventSource.close();
      setDriverStatus('pending');
    };

    return () => {
      console.log('🔌 SSE connection closed');
      eventSource.close();
    };
  }, [truckDriverid, stockNo, tripId]);

  // Fetch truck driver data
  useEffect(() => {
    if (!truckDriverid) {
      console.error('No truckDriverid provided');
      setLoading(false);
      return;
    }

    const fetchTruckDriver = async () => {
      try {
        console.log('Fetching data for truckDriverid:', truckDriverid);
        const response = await getTruckDriver(truckDriverid);
        console.log('API Response:', response.data.data);
        setTruckDriverDetails(response.data.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching truck driver data:', error);
        setLoading(false);
      }
    };

    const fetchTrack = async () => {
      if (!stockNo || !tripId) return;

      try {
        const response = await getTrack(truckDriverid, stockNo, tripId);
        console.log('Tracking Data for track:', response);
      } catch (error) {
        console.error('Error fetching Tracking data:', error);
      }
    };

    const fetchLocations = async () => {
      if (!stockNo || !tripId) return;

      try {
        console.log(
          'Fetching past locations for:',
          truckDriverid,
          stockNo,
          tripId
        );
        const response = await getLocations(truckDriverid, stockNo, tripId);
        console.log('Past Locations Data:', response.data);

        if (response.data && Array.isArray(response.data.data)) {
          const formattedLocations = response.data.data.map((loc) => ({
            lat: loc.latitude || loc.lat,
            lon: loc.longitude || loc.lon,
            timestamp: loc.timestamp,
          }));
          setPastLocations(formattedLocations);
        } else if (response.data && Array.isArray(response.data)) {
          const formattedLocations = response.data.map((loc) => ({
            lat: loc.latitude || loc.lat,
            lon: loc.longitude || loc.lon,
            timestamp: loc.timestamp,
          }));
          setPastLocations(formattedLocations);
        }
      } catch (error) {
        console.error('Error fetching past locations:', error);
      }
    };

    fetchTruckDriver();
    fetchTrack();
    fetchLocations();
  }, [truckDriverid, stockNo, tripId]);

  // Initialize Mapbox map (only if truck driver details are available)
  useEffect(() => {
    if (!truckDriverDetails || !truckDriverDetails.coordinates || !showTrack)
      return;

    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: truckDriverDetails.coordinates || [-118.2437, 34.0522],
      zoom: 10,
    });

    map.addControl(new mapboxgl.NavigationControl());

    new mapboxgl.Marker()
      .setLngLat(truckDriverDetails.coordinates || [-118.2437, 34.0522])
      .addTo(map);

    return () => map.remove();
  }, [truckDriverDetails, showTrack]);

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'live':
        return 'success';
      case 'past':
        return 'secondary';
      case 'pending':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Handle Show Track button click
  const handleShowTrack = () => {
    setShowTrack(true);
  };

  return (
    <React.Fragment>
      <section
        className="section bg-light"
        style={{ padding: '80px' }}
      >
        <Row>
          <Col md="4">
            {loading ? (
              <CardText className="text-muted">Loading...</CardText>
            ) : truckDriverDetails ? (
              <Card className="shadow-sm mb-4">
                {drivers.map((driver) => (
                  <CardBody key={truckDriverid}>
                    <div className="d-flex align-items-center justify-content-between mb-4">
                      <div className="d-flex align-items-center">
                        <div className="bg-primary text-white rounded-circle p-3 me-3">
                          {driver.avatar}
                        </div>
                        <div>
                          <h5 className="mb-1">
                            {truckDriverDetails.firstName || 'Unknown'}
                          </h5>
                          <small className="text-muted">
                            <MapPin
                              size={14}
                              className="me-1"
                            />
                            {driver.location}
                          </small>
                        </div>
                      </div>
                      <Badge color={getStatusBadgeColor(driver.status)}>
                        {driver.status}
                      </Badge>
                    </div>

                    <Card className="bg-light border-0 mb-4">
                      <CardBody>
                        <div className="d-flex align-items-center mb-2">
                          <Car
                            size={18}
                            className="me-2 text-muted"
                          />
                          <strong>{driver.vehicle}</strong>
                        </div>
                        <ListGroup
                          flush
                          className="bg-transparent"
                        >
                          <ListGroupItem className="bg-transparent px-0">
                            <span>Plate: {driver.plate}</span>
                          </ListGroupItem>
                          <ListGroupItem className="bg-transparent px-0">
                            <span>Year: {driver.year}</span>
                          </ListGroupItem>
                          <ListGroupItem className="bg-transparent px-0">
                            <span>Color: {driver.color}</span>
                          </ListGroupItem>
                        </ListGroup>
                      </CardBody>
                    </Card>

                    <ListGroup
                      flush
                      className="mb-4"
                    >
                      <ListGroupItem className="d-flex align-items-center px-0">
                        <Phone
                          size={16}
                          className="me-2 text-muted"
                        />
                        {driver.phone}
                      </ListGroupItem>
                      <ListGroupItem className="d-flex align-items-center px-0">
                        <Mail
                          size={16}
                          className="me-2 text-muted"
                        />
                        {driver.email}
                      </ListGroupItem>
                      <ListGroupItem className="d-flex align-items-center px-0">
                        <Calendar
                          size={16}
                          className="me-2 text-muted"
                        />
                        Registered: {formatDate(driver.date)}
                      </ListGroupItem>
                    </ListGroup>

                    <div className="d-flex gap-2">
                      <Button
                        color="primary"
                        block
                        onClick={handleShowTrack}
                      >
                        Show Track
                      </Button>
                      <Button
                        color="secondary"
                        block
                      >
                        Contact
                      </Button>
                    </div>
                  </CardBody>
                ))}
              </Card>
            ) : (
              <CardText className="text-danger">
                No driver data available for ID: {truckDriverid || 'Unknown'}
              </CardText>
            )}
          </Col>

          <Col md="8">
            <Card>
              <CardBody>
                <CardTitle tag="h5">Live Truck Tracking</CardTitle>
                {showTrack ? (
                  <>
                    <div
                      ref={mapContainer}
                      style={{ height: '25px', width: '100%' }}
                    />
                    <MapDisplay2
                      pathCoordinates={pathCoordinates}
                      pastLocations={pastLocations}
                    />
                  </>
                ) : (
                  <CardText>
                    Click "Show Track" to view the tracking map.
                  </CardText>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </section>
    </React.Fragment>
  );
};

export default TruckTracking;